import { MouseEvent, useCallback, useMemo, useState } from 'react';

import { Popover } from '@mui/material';

import {
  Button,
  Icons,
  List,
  ListItem,
  ListItemText,
  ListSearchInput,
  Scroller,
  Box,
} from '@cast/design-system';
import { HibernationSchedule } from '@cast/types-cluster-autoscaler';

type SavedSchedulesPickerProps = {
  schedules?: HibernationSchedule[];
  selectedSchedule: HibernationSchedule | null;
  onSelect: (schedule: HibernationSchedule) => void;
};

export const SavedSchedulesPicker = ({
  schedules,
  selectedSchedule,
  onSelect,
}: SavedSchedulesPickerProps) => {
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const isPopoverOpen = Boolean(anchorEl);
  const [searchInputValue, setSearchInputValue] = useState<string>('');

  const handleOpen = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  }, []);

  const closePopover = useCallback((event: MouseEvent<HTMLElement>) => {
    setAnchorEl(null);
    event.stopPropagation();
  }, []);

  const handleOptionSelect = useCallback(
    (event: MouseEvent<HTMLElement>, selectedSchedule: HibernationSchedule) => {
      closePopover(event);

      onSelect(selectedSchedule);
    },
    [onSelect, closePopover]
  );

  const filteredData = useMemo(
    () =>
      schedules?.filter(({ name }) =>
        name.toLowerCase().includes(searchInputValue.toLowerCase())
      ) ?? [],
    [schedules, searchInputValue]
  );

  return (
    <>
      <Button
        testId="import-from-saved-schedule-button"
        variant="secondary"
        size="sm"
        endIcon={<Icons.FileArrowUp />}
        onClick={handleOpen}
      >
        Import from saved schedule
      </Button>
      <Popover
        open={isPopoverOpen}
        anchorEl={anchorEl}
        onClose={closePopover}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        disableScrollLock
      >
        <Box width="213px">
          <ListSearchInput
            autoFocus
            autoComplete="off"
            size="small"
            placeholder="Search schedule"
            listItemProps={{
              sx: { '&.Mui-focusVisible': { background: 'none' } },
            }}
            value={searchInputValue}
            onChange={(_, v) => setSearchInputValue(v)}
            showClearButton
            clearButtonProps={{
              onClick: () => setSearchInputValue(''),
            }}
          />

          <List size="medium" disablePadding testId="schedule-chooser-list">
            {filteredData.length ? (
              <Scroller
                sx={{ gap: '2px', overflow: 'auto', maxHeight: '225px' }}
              >
                {filteredData.map((option) => {
                  return (
                    <ListItem
                      key={option.id}
                      onClick={(e) => handleOptionSelect(e, option)}
                      selected={option.id === selectedSchedule?.id}
                    >
                      <ListItemText
                        primary={option.name}
                        primaryTypographyProps={{ variant: 'P12M' }}
                      />
                    </ListItem>
                  );
                })}
              </Scroller>
            ) : (
              <ListItem sx={{ p: 8 }}>
                <ListItemText
                  primary="No schedules found"
                  primaryTypographyProps={{ variant: 'P12M' }}
                  secondary="Adjust your search criteria"
                  secondaryTypographyProps={{ variant: 'P12R' }}
                />
              </ListItem>
            )}
          </List>
        </Box>
      </Popover>
    </>
  );
};
